import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  Signal,
} from '@angular/core';

import { RouterModule } from '@angular/router';
import { AlertTheme, AlertConfig, AlertEventType } from './alert.model';
import { IconComponent } from '../../icons';

/**
 * Alert component that displays messages with different themes, optional buttons, and navigation links.
 */
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'da-alert',
    imports: [NgClass, RouterModule, IconComponent],
    templateUrl: './alert.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertComponent {
  /** The theme of the alert, which determines styling and icons. */
  public theme = input<AlertTheme>('info');

  /** Configuration object defining the content, buttons, and structure of the alert. */
  public config = input.required<AlertConfig>();

  /** Event emitter for handling button clicks inside the alert component. */
  public events = output<{ eventType: AlertEventType; id: string }>();

  public icon: Signal<string> = computed(() =>
    this.configureIcon(this.theme())
  );
  public themeClass: Signal<string> = computed(() => {
    const themeMap: { [key in AlertTheme]: string } = {
      info: 'neutral',
      highlight: 'purple',
      success: 'green',
      warning: 'yellow',
      error: 'red',
    };

    return themeMap[this.theme()];
  });
  public hasSubtitle: Signal<boolean> = computed(
    () => !!this.config().subTitle
  );
  public hasTitleClass: Signal<boolean> = computed(() => this.hasSubtitle());
  public isComplex: Signal<boolean> = computed(
    () => this.hasSubtitle() || !!this.config().items || !!this.config().buttons
  );
  public wrapperClass: string;
  public emphasisClass: string;

  /**
   * Handles button click events and emits an event with the button ID.
   * @param eventType The type of event triggered.
   * @param id The identifier of the clicked button.
   */
  public eventHandler(eventType: AlertEventType, id: string): void {
    this.events.emit({ eventType, id });
  }

  private configureIcon(theme: AlertTheme): string {
    switch (theme) {
      case 'success':
        return 'check-circle';
      case 'warning':
        return 'exclamation-triangle';
      case 'error':
        return 'exclamation-circle';
      default:
        return 'information-circle';
    }
  }
}
